// Browser voice output abstraction — Web Speech Synthesis API (Phase 1).
// The rest of the application only ever calls speak(text) and stop().
// To upgrade to Google Cloud TTS (Phase 3): change only this file.

const DEFAULT_VOICE_NAME = 'Flo (English (United Kingdom))';

// Strips characters that don't read well aloud:
//   - emoji (any extended pictograph)
//   - markdown emphasis: **, *, `, ~
//   - markdown headers: leading #
//   - markdown blockquote markers: leading >  (so '> hi' is read as 'hi',
//     not 'greater than hi')
//   - markdown table separator rows: lines like `|---|---|`
//   - markdown table cell pipes: replaced with a space
// Then collapses any whitespace runs to single spaces. Punctuation is
// preserved so the synthesiser still gets natural pauses.
// Mirrors server/services/voice/stripForSpeech.js — keep the regex set
// in sync with that file.
export function stripForSpeech(text) {
  if (!text) return '';
  return String(text)
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/\*+/g, '')
    .replace(/`+/g, '')
    .replace(/~+/g, '')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*>+\s?/gm, '')
    .replace(/^\s*\|?[\s|:-]*-[\s|:-]*\|?\s*$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Picks a voice from the system's available list using the fallback chain:
//   1. Exact name match for the requested voice
//   2. Exact name match for the project default ('Flo (English (United Kingdom))')
//   3. Any en-GB voice
//   4. Any en-* voice
//   5. null → caller leaves the utterance.voice unset and the system default plays
export function resolveVoice(voices, requestedName) {
  if (!voices || voices.length === 0) return null;

  const tryNames = [requestedName, DEFAULT_VOICE_NAME].filter(Boolean);
  for (const name of tryNames) {
    const match = voices.find(v => v.name === name);
    if (match) return match;
  }

  const enGB = voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en-gb'));
  if (enGB) return enGB;

  const en = voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en'));
  if (en) return en;

  return null;
}

export class VoiceOutput {
  constructor() {
    this._utterance = null;
  }

  isSupported() {
    try {
      return !!window.speechSynthesis;
    } catch {
      return false;
    }
  }

  // Speaks the cleaned form of the provided text — never adds system content
  // (VOICE-07). Returns a Promise that resolves when speech completes or
  // rejects on error. Empty/whitespace-only text resolves immediately.
  //
  // Options:
  //   voiceName  — preferred voice; resolved via resolveVoice() with fallback
  //   rate       — default 0.9
  //   pitch      — default 1.0
  speak(text, options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isSupported()) {
        reject(new Error('Speech synthesis is not supported in this browser.'));
        return;
      }

      this.stop();

      const cleaned = stripForSpeech(text);
      if (!cleaned) {
        resolve();
        return;
      }

      const utterance = new window.SpeechSynthesisUtterance(cleaned);
      utterance.lang  = 'en-GB';
      utterance.rate  = options.rate  ?? 0.9;
      utterance.pitch = options.pitch ?? 1.0;

      const voices = this.getAvailableVoices();
      const voice = resolveVoice(voices, options.voiceName);
      if (voice) utterance.voice = voice;

      utterance.onend   = () => resolve();
      utterance.onerror = (e) => reject(new Error(e.error || 'Speech synthesis error'));

      this._utterance = utterance;
      window.speechSynthesis.speak(utterance);
    });
  }

  stop() {
    if (window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        // already stopped — safe to ignore
      }
    }
    this._utterance = null;
  }

  getAvailableVoices() {
    if (!this.isSupported()) return [];
    try {
      return window.speechSynthesis.getVoices();
    } catch {
      return [];
    }
  }
}
